import { ButtonHTMLAttributes, FC } from 'react';
import classNames from 'classnames';

import SeeMoreIconButton from '@z-components/atoms/SeeMoreIconButton';
import VerticalButton from '../../atoms/VerticalButton';

import styles from './VerticalTabButtonGroup.module.scss';
import { Link } from 'routes';

interface Item {
  id: string;
  label: string;
  onClick?: ButtonHTMLAttributes<HTMLButtonElement>['onClick'];
}

interface Props {
  areVerticalsCollapsed: boolean;
  background: boolean;
  items: Array<Item>;
  language: string;
  onItemClick?: (itemId: string) => void;
  selectVertical: (vertical: string) => void;
  selectedItem: string;
  setAreVerticalsCollapsed: (areVerticalsCollapsed: boolean) => void;
  slug: string;
}

export const VerticalTabButtonGroup: FC<Props> = ({
  areVerticalsCollapsed,
  setAreVerticalsCollapsed,
  background,
  items,
  language,
  slug,
  onItemClick = () => undefined,
  selectedItem,
  // eslint-disable-next-line
  selectVertical,
}) => {
  if (items.length === 0) {
    return null;
  }

  const getCollapsedVerticalLabel = () =>
    items.find((item) => item.id === selectedItem)?.label ?? selectedItem;
  return (
    <div
      className={classNames(styles['z-vertical-tab-button-group'], {
        [styles['z-vertical-tab-button-group-background']]: background,
        [styles['z-vertical-tab-button-group-collapsed']]: areVerticalsCollapsed,
      })}
    >
      {areVerticalsCollapsed && selectedItem && (
        <div className={styles['z-vertical-tab-button-group-collapsed-container']}>
          <VerticalButton
            className={styles['z-vertical-tab-button-group-collapsed-button']}
            label={getCollapsedVerticalLabel()}
            // eslint-disable-next-line react/jsx-no-bind
            onClick={() => onItemClick(selectedItem)}
            selected={true}
          />
          {items.length > 1 && (
            <span className={styles['z-vertical-tab-button-group-collapsed-container-see-more']}>
              <SeeMoreIconButton
                // eslint-disable-next-line react/jsx-no-bind
                onClick={() => setAreVerticalsCollapsed(false)}
              />
            </span>
          )}
        </div>
      )}
      {!areVerticalsCollapsed && items.length > 0 && (
        <div className={styles['z-vertical-tab-button-group-item-list']}>
          {items.map((item) => (
            // eslint-disable-next-line jsx-a11y/no-static-element-interactions
            <span
              className={`${styles['z-vertical-tab-button-group-item']}`}
              key={`vtbg_item_${item.id}`}
              onClick={() => {
                onItemClick(item.id);
                setAreVerticalsCollapsed(true);
                selectVertical(item.id);
              }}
            >
              <div
                className={classNames({
                  [styles['z-vertical-button-selected']]: item.id === selectedItem,
                  [styles['z-vertical-button-unselected']]: item.id !== selectedItem,
                })}
              >
                <Link
                  prefetch={false}
                  route={`${window.location.origin}${
                    language === 'en' ? '/en' : ''
                  }/profile/${slug}/pro${item.id ? `/${item.id}` : ''}`}
                >
                  <a
                    className={`${styles['z-vertical-button-label']}`}
                    data-cy={'popular-vertical'}
                  >
                    {item.label}
                  </a>
                </Link>
              </div>
            </span>
          ))}
        </div>
      )}
    </div>
  );
};

export default VerticalTabButtonGroup;
export type { Props as VerticalTabButtonGroupProps };
